import PropTypes from 'prop-types';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import i18n from '~/i18n';
import logoPNG from '~/images/logo.jpeg';

const PageHeaderMeta = props => {
	let location = useLocation();
	const isProduction = __BW_ENV__.REACT_APP_ENV === 'production';
	const isBrandPage = props.type === 'product.group';
	const normalizedPath = (location.pathname ?? '/').replace(/\/+$/, '') || '/';
	const isHomePage = normalizedPath === '/' || normalizedPath === '/en';
	const title = isHomePage
		? (props.page_title ?? '')
		: isBrandPage && props.page_title
			? props.page_title
			: props.page_title
				? props.page_title + ' - UndergroundShop'
				: 'UndergroundShop';
	const type = props.type ? props.type : 'website';
	const url = 'https://www.undergroundshop.gr' + location.pathname;
	const image = props.image ? props.image : window.location.origin + logoPNG;
	const locale = i18n.language === 'el' ? 'el_GR' : 'en_US';
	const status = props.status;

	const renderMetaKeywords = () => {
		if (props.meta_keywords != null) {
			return <meta name="keywords" content={props.meta_keywords} />;
		}

		return null;
	};

	const renderMetaDescription = () => {
		if (props.meta_description != null) {
			return <meta name="description" content={props.meta_description} />;
		}

		return null;
	};
	const renderOpenGraphDescription = () => {
		if (props.meta_description != null) {
			return <meta property="og:description" content={props.meta_description} />;
		}

		return null;
	};

	const renderMetaTitle = () => {
		if (props.meta_title != null) {
			return <meta name="meta-title" content={props.meta_title} />;
		}

		return null;
	};
	const renderMetaHeaderTitle = () => {
		if (props.header_title != null) {
			return <meta name="header-title" content={props.header_title} />;
		}

		return null;
	};

	const renderProductPrice = () => {
		if (props.product_price != null) {
			return <meta property="product:price:amount" content={props.product_price} />;
		}

		return null;
	};
	const renderProductCurrency = () => {
		if (props.product_price != null) {
			return <meta property="product:price:currency" content="EUR" />;
		}

		return null;
	};

	const renderFaqSchema = () => {
		if (props.faq_schema != null) {
			return <script type="application/ld+json">{JSON.stringify(props.faq_schema)}</script>;
		}
	};

	const renderSchemas = () => {
		if (Array.isArray(props.schemas) && props.schemas.length > 0) {
			return props.schemas.map((schema, i) => (
				<script key={i} type="application/ld+json">
					{typeof schema === 'string' ? schema : JSON.stringify(schema)}
				</script>
			));
		}

		if (props.schema) {
			return <script type="application/ld+json">{typeof props.schema === 'string' ? props.schema : JSON.stringify(props.schema)}</script>;
		}

		return null;
	};

	const renderRobotsMeta = () => {
		if (!isProduction) {
			<meta name="robots" content="noindex,nofollow"></meta>;
		}

		if (i18n.language === 'en' && location.pathname.startsWith('/en/')) {
			return <meta name="robots" content="noindex,nofollow" />;
		}

		if (props.disable_indexation || props.disable_follow) {
			const content = `${props.disable_indexation ? 'noindex' : 'index'},${props.disable_follow ? 'nofollow' : 'follow'}`;
			return <meta name="robots" content={content} />;
		}
		return null;
	};

	const renderCanonicalLink = () => {
		if (props.canonical_url) {
			return <link rel="canonical" href={props.canonical_url} />;
		}
		return <link rel="canonical" href={url} />;
	};

	const lang = localStorage.getItem('I18N_LANGUAGE');

	return (
		<HelmetProvider>
			<Helmet>
				<html lang={lang ?? 'el'} />
				<meta charSet="utf-8" />
				{/*<meta name="robots" content="noindex" />*/}
				<title>{title}</title>
				<meta property="og:site_name" content="Undergroundshop" />
				<meta property="og:title" content={title} />
				<meta property="og:type" content={type} />
				<meta property="og:url" content={url} />
				<meta property="og:image" content={image} />
				<meta property="og:image:url" content={image} />
				<meta property="og:locale" content={locale} />
				{status && <meta name="prerender-status-code" content={status} />}

				{renderRobotsMeta()}
				{renderCanonicalLink()}
				{renderMetaKeywords()}
				{renderMetaDescription()}
				{renderOpenGraphDescription()}
				{renderProductPrice()}
				{renderProductCurrency()}
				{renderFaqSchema()}
				{renderSchemas()}
				{renderMetaTitle()}
				{renderMetaHeaderTitle()}
			</Helmet>
		</HelmetProvider>
	);
};

PageHeaderMeta.propTypes = {
	page_title: PropTypes.string,
	meta_keywords: PropTypes.string,
	meta_description: PropTypes.string,
	type: PropTypes.string,
	image: PropTypes.string,
	product_price: PropTypes.number,
	status: PropTypes.number,
	header_title: PropTypes.string,
	meta_title: PropTypes.string,
	schema: PropTypes.any,
	schemas: PropTypes.arrayOf(PropTypes.any),
	disable_indexation: PropTypes.bool,
	disable_follow: PropTypes.bool,
	canonical_url: PropTypes.string,
	faq_schema: PropTypes.object
};

export default PageHeaderMeta;
